import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { cloneDeep, isEqual } from 'lodash';

import { Loader } from '../../misc/loader-utils';
import { setSnackbar } from '../../../redux/actions';
import Modal from '../../base/modals/base-modal';
import { api } from '../../../services/api';
import { dateService } from '../../../services/date';
import { session } from '../../../services/session';

import { formatValue } from '../../base/common/utils';

import DefaultStrategy from './default-strategy';
import Strategy from './strategy';
import Pagination from '../../base/common/pagination';

import './merchant-strategies.scss';

class MerchantStrategies extends Component {
  constructor(props) {
    super(props);

    this.orgId = session.orgId;
    const today = dateService.getCurrentDateTime();

    this.merchantInfoEndpoint = `/api/v0/merchants/${this.orgId}/`;
    this.budgetsEndpoint = `/api/v0/merchants/${this.orgId}/budgets/`;
    this.strategiesEndpoint = `/api/v0/merchants/${this.orgId}/strategy/`;
    this.typeAheadEndpoint = `/api/v0/merchants/${this.orgId}/strategy/type_ahead/`;
    this.netMarginsEndpoint = '/api/v0/admins/stats/net_margins/';

    this.getBudgetParams = {
      date_from: today.format('YYYY-MM'),
      date_to: today.add(1, 'month').format('YYYY-MM'),
    };

    this.getStrategiesParams = () => ({
      page: this.state.pageInfo.page,
      per_page: this.state.pageInfo.per_page,
      get_spend: this.showBudgetSpent,
    });

    // Dev toggle to adjust performance of strategies page.
    this.showBudgetSpent = true;

    // Matches price formats: $x,xxx.xx, xx.x, etc.
    this.currencyRegex = /^\$?\d*(?:,?\d{3})*(?:\.\d{1,2})?$/g;
    // Matches positive integers and floats up to two decimal places
    this.roiRegex = /^\d{0,6}(?:\.\d{0,2})?$/;

    // Ensure CPC and ROI values remain under $1000
    this.maxStrategyValue = 1000;

    // Ensure Budget values remain under $9,999,999.99
    this.maxBudget = 9999999.99;

    this.msgs = {
      // Errors
      strategyName: 'A strategy name is required.',
      trigger: 'Brand or Category is required.',
      roi: 'Invalid ROI value.',
      cpc: 'Invalid CPC value.',
      startDate: 'Start date is required.',
      date: 'Start date must be before end date.',
      budgetAmount: 'Invalid budget amount.',
      utm: 'UTM string must be less than 512 characters.',
      addStrategy: 'Please cancel or save working strategy before adding a new one.',
      // Confirmations
      saved: 'Campaign edits saved.',
    };
    this.cogsError =
      'When setting a COGS value, a publisher needs to be selected.' +
      ' Please select a publisher and re-save.';
    this.missingError =
      'A campaign must have a Campaign Name, a ROI/CPC value, and a Start Date.  At least one' +
      ' of these required fields is currently missing.  Please revise your campaign to include' +
      ' all required fields, and save again.';

    this.defaultState = {
      // Default Strategy Info
      merchantInfo: {}, // API response
      strategyType: null, // [ROI, CPC]
      roiGoal: null, // ROI input value
      manualClickValue: null, // CPC input value
      strategyValueInput: null, // [roiGoal, manualClickValue, '']

      // Default Budget Info
      merchantBudget: {}, // API response
      budgetId: null, // budget ID
      budgetAmount: null, // budget amount
      budgetAmountInput: null, // input for budget amount
      budgetSpent: null, // budget amount spent
      yearMonth: null, // current month

      // Merchant Strategies
      merchantStrategies: {},
      strategies: [],

      // Errors
      defaultStrategyErrors: {
        roi: false,
        cpc: false,
        budgetAmount: false,
        addStrategy: false,
      },
      strategyErrors: {
        strategyName: false,
        trigger: false,
        roi: false,
        cpc: false,
        startDate: false,
        date: false,
        budgetAmount: false,
        utm: false,
      },

      // Snackbar
      snackbarMsg: null,

      strategyChangesMade: {},
      pageInfo: {
        page: 1,
        per_page: 20,
        total_items: null,
        total_page: null,
      },
    };

    this.state = {
      loading: true,
      changesMade: false,
      saveInProgress: false,
      errorModal: null, // strategy error modal for BE response
      initialPubs: [],
      ...this.defaultState,
    };

    this.merchantStrategiesContainer = React.createRef();
  }

  async componentDidMount() {
    await this.getAndSetStrategies();

    // Deep linking to new strategy in nav bar
    if (this.props.match.params.action === 'new') {
      this.addStrategy();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // Deep linking to new strategy in nav bar
    const prevAction = prevProps.match.params.action;
    const currAction = this.props.match.params.action;

    if (prevAction !== currAction && currAction === 'new') {
      this.addStrategy();
    }

    //  Hacky Solution to prevent duplicate getAndSetStrategies on page load
    if (
      prevState.pageInfo.page !== this.state.pageInfo.page ||
      prevState.pageInfo.per_page !== this.state.pageInfo.per_page
    ) {
      this.getAndSetStrategies();
    }
  }

  getMerchantDetails() {
    return Promise.all([
      api.get(this.merchantInfoEndpoint),
      api.get(this.budgetsEndpoint, this.getBudgetParams),
      api.get(this.strategiesEndpoint, this.getStrategiesParams()),
      api.get(this.typeAheadEndpoint, { type: 'publisher' }),
    ]).then(([merchInfo, merchBudget, merchStrats, merchPubs]) => {
      const merchantInfo = merchInfo.data.data[0];
      const merchantBudget = merchBudget.data.data[0];
      const merchantStrategies = merchStrats.data.data[0];
      const merchantPublishers = merchPubs.data.data[0];

      return Promise.resolve([
        merchantInfo,
        merchantBudget,
        merchantStrategies,
        merchantPublishers,
      ]);
    });
  }

  getAndSetStrategies() {
    return this.getMerchantDetails().then(
      async ([merchantInfo, merchantBudget, merchantStrategies, merchantPublishers]) => {
        await this.setStrategyInfo(merchantInfo);
        await this.setBudgetInfo(merchantBudget);
        await this.setMerchantStrategies(merchantStrategies);
        await this.setState({ initialPubs: merchantPublishers.stats });

        this.setState({
          ...this.defaultState,
          loading: false,
        });
      },
    );
  }

  getAndSetNetMargins() {
    const strategyIds = this.state.strategies.map(strats => parseInt(strats.strategy_id));
    if (!strategyIds.length) return;

    const dates = dateService.getPresetDateRanges().thirtyDay;
    const params = {
      strategy_ids: strategyIds.join(','),
      date_from: dateService.formatDate(dates.startDate),
      date_to: dateService.formatDate(dates.endDate),
    };

    api.get(this.netMarginsEndpoint, params).then(resp => {
      const netMarginsStats = resp.data.data[0].stats;
      const marginsByStrategyId = netMarginsStats.reduce((obj, item) => {
        return { ...obj, [item.strategy_id]: item };
      }, {});

      const strategiesCopy = cloneDeep(this.state.strategies);
      strategiesCopy.map(strategy => {
        strategy.netMargin = this.getExpectedMargin(strategy, marginsByStrategyId);
        return strategy;
      });

      this.setState({
        strategies: strategiesCopy,
      });
    });
  }

  getExpectedMargin(strategy, marginsByStrategyId) {
    let marginGoal = null;
    // If there are marginal stats, use nrtv_net_margin_percentage
    if (
      marginsByStrategyId[strategy.strategy_id] &&
      marginsByStrategyId[strategy.strategy_id].nrtv_net_margin_percentage &&
      marginsByStrategyId[strategy.strategy_id].nrtv_net_margin_percentage !== '0.00'
    ) {
      marginGoal = marginsByStrategyId[strategy.strategy_id].nrtv_net_margin_percentage;
      // Custom margin calculation for CPC pricing and EPC payment
    } else if (strategy.pricing_model === 'CPC' && strategy.payment_model === 'EPC') {
      const cogs = strategy.cogs ? strategy.cogs : 0.6; // Hard-coded for now until BE handles margin calc
      marginGoal =
        strategy.bid && strategy.publisher_epc
          ? Math.round((1 - (cogs * strategy.publisher_epc) / strategy.bid).toFixed(2) * 100)
          : null;
      // Use strategy cogs if there's one set, else use default cogs (calculated into margin_goal field)
    } else if (strategy.pricing_model === 'EPP' && strategy.payment_model !== 'EPC') {
      marginGoal = marginsByStrategyId[strategy.strategy_id]
        ? marginsByStrategyId[strategy.strategy_id].margin_goal
        : this.calcRoiMargin(strategy);
    }

    marginGoal = marginGoal === '0.00' ? null : marginGoal;
    return marginGoal;
  }

  calcRoiMargin(strategy) {
    if (!(strategy.roi_goal && strategy.publisher_roi_target)) return null;
    const cogs = strategy.cogs ? strategy.cogs : 0.6; // Hard-coded for now until BE handles margin calc
    return Math.round(
      (1 - (cogs * strategy.roi_goal) / strategy.publisher_roi_target).toFixed(2) * 100,
    );
  }

  setStrategyInfo(merchantInfo) {
    const isFlatRateMerchant =
      merchantInfo.pricing_model === 'EPP' && merchantInfo.manual_click_value !== null;
    const flatOrCpc = isFlatRateMerchant || merchantInfo.pricing_model === 'CPC';

    this.defaultState = {
      ...this.defaultState,
      merchantInfo,
      roiGoal: merchantInfo.roi_goal,
      manualClickValue: merchantInfo.manual_click_value,
      strategyType: flatOrCpc ? 'cpc' : 'roi',
      strategyValueInput: flatOrCpc
        ? formatValue(merchantInfo.manual_click_value, 'currency') || ''
        : merchantInfo.roi_goal,
    };
  }

  setBudgetInfo(merchantBudget) {
    const thisMonth = dateService.getCurrentDateTime().format('YYYY-MM');
    const currentMonth =
      (merchantBudget.budgets ? merchantBudget.budgets[thisMonth] : merchantBudget) || {};

    const currentBudget = currentMonth.budget_amount;
    this.defaultState = {
      ...this.defaultState,
      merchantBudget,
      budgetId: currentMonth.budget_id || null,
      budgetAmount: currentBudget || '',
      budgetAmountInput: currentBudget ? formatValue(currentBudget, 'currency') : '',
      budgetSpent: parseFloat(currentMonth.spent_amount || '').toFixed(2),
      yearMonth: thisMonth,
    };
  }

  setMerchantStrategies(merchantStrategies, update = false) {
    const strategiesMap = update ? { ...this.defaultState.merchantStrategies } : {};
    merchantStrategies.strategies.forEach(strat => {
      strategiesMap[strat.strategy_id.toString()] = strat;
      // Format cogs into percentage
      if (strat.cogs && strat.cogs <= 1) {
        strat.cogs *= 100;
      }
    });

    const strategies = Object.values(strategiesMap);

    this.defaultState = {
      ...this.defaultState,
      merchantStrategies: strategiesMap,
      strategies: strategies.length ? strategies : [],
    };

    if (!update) {
      this.defaultState.pageInfo = merchantStrategies.page_info;
    }
  }

  // Default Strategy Functions
  selectStrategyType(type) {
    const strategyValueMap = {
      roi: this.state.roiGoal,
      cpc: formatValue(this.state.manualClickValue, 'currency'),
    };

    this.setState({
      strategyType: type,
      strategyValueInput: strategyValueMap[type] || '',
      changesMade: true,
      defaultStrategyErrors: {
        ...this.state.defaultStrategyErrors,
        [type === 'roi' ? 'cpc' : 'roi']: false, // Reset errors on other strategy
      },
    });
  }

  validateStrategyInput(value) {
    const input = value.toString();
    const floatInput = parseFloat(input.replace(/\$|,/g, ''));
    const type = this.state.strategyType;

    const isValidInput =
      type === 'cpc'
        ? input && floatInput < this.maxStrategyValue && input.match(this.currencyRegex)
        : input && floatInput <= this.maxStrategyValue && input.match(this.roiRegex);

    this.setState(currentState => {
      return {
        ...(isValidInput && {
          [type === 'cpc' ? 'manualClickValue' : 'roiGoal']: input.replace(/\$|,/g, ''),
          strategyValueInput:
            type === 'cpc' && value !== ''
              ? formatValue(input.replace(/\$|,/g, ''), 'currency')
              : value,
        }),
        defaultStrategyErrors: {
          ...currentState.defaultStrategyErrors,
          [type]: !isValidInput,
        },
      };
    });

    return isValidInput;
  }

  updateStrategyValue(e) {
    this.setState({
      strategyValueInput: e.target.value,
      changesMade: true,
    });
  }

  // Default Budget Functions
  validateBudgetInput(input) {
    const inputValue = input || '';
    const isValidBudget =
      parseFloat(inputValue.replace(/\$|,/g, '')) <= this.maxBudget &&
      inputValue.match(this.currencyRegex);

    this.setState(currentState => {
      // Get rid of pretty currency formatting for backend payload
      const budgetAmount = parseFloat(inputValue.replace(/\$|,/g, '')).toFixed(2);
      return {
        ...(isValidBudget && {
          budgetAmount,
          budgetAmountInput: formatValue(budgetAmount, 'currency'),
        }),
        defaultStrategyErrors: {
          ...currentState.defaultStrategyErrors,
          budgetAmount: !isValidBudget,
        },
      };
    });

    return isValidBudget;
  }

  updateBudgetValue(e) {
    this.setState({
      budgetAmountInput: e.target.value,
      changesMade: true,
    });
  }

  // Reset Default Strategy State Changes
  cancelChanges() {
    this.setState({
      changesMade: false,
      strategyType: this.defaultState.strategyType,
      budgetAmount: this.defaultState.budgetAmount,
      budgetAmountInput: this.defaultState.budgetAmountInput,
      roiGoal: this.defaultState.roiGoal,
      manualClickValue: this.defaultState.manualClickValue,
      strategyValueInput: this.defaultState.strategyValueInput,
      defaultStrategyErrors: this.defaultState.defaultStrategyErrors,
    });
  }

  // Save Default Strategy State Changes
  async saveDefaultStrategy() {
    if (!this.hasNoError('defaultStrategyErrors'))
      return this.triggerSnackbar('defaultStrategyErrors');

    // Update Strategy Type and Value
    if (!isEqual(this.defaultState.strategyValueInput, this.state.strategyValueInput)) {
      const input =
        this.state.strategyValueInput === ''
          ? null
          : this.state.strategyType === 'roi'
          ? parseFloat(this.state.strategyValueInput)
          : parseFloat(this.state.strategyValueInput.replace(/\$|,/g, '')).toFixed(2);

      const params =
        this.state.merchantInfo.pricing_model === 'EPP' && this.state.strategyType === 'roi'
          ? { roi_goal: input, manual_click_value: null }
          : { manual_click_value: input };

      await api.put(this.merchantInfoEndpoint, params).then(resp => {
        this.setStrategyInfo(resp.data.data[0]);
      });
    }

    // Update Budget Amount
    if (!isEqual(this.defaultState.budgetAmount, this.state.budgetAmount)) {
      const params = { budget_amount: this.state.budgetAmount, year_month: this.state.yearMonth };

      const updateBudget = this.state.budget_id
        ? api.put(`${this.budgetsEndpoint}${this.state.budgetId}/`, params)
        : api.post(`${this.budgetsEndpoint}`, params);

      await updateBudget.then(resp => {
        this.setBudgetInfo(resp.data.data[0]);
      });
    }

    this.setState(
      {
        ...this.defaultState,
        changesMade: false,
      },
      () => {
        this.triggerSnackbar(null, 'saved');
      },
    );
  }

  // Error handling
  setError(errors) {
    this.setState({
      strategyErrors: {
        ...this.state.strategyErrors,
        ...errors,
      },
    });
  }

  hasNoError(errorGroup) {
    if (errorGroup === 'defaultStrategyErrors') {
      // TODO: only Bandaid fix for onBlur race condition with onClick form submit, refactor
      const isBudgetInputValid = this.validateBudgetInput(this.state.budgetAmountInput);
      if (!isBudgetInputValid) return false;
    }
    return Object.keys(this.state[errorGroup]).every(i => !this.state[errorGroup][i]);
  }

  triggerSnackbar(errorGroup, errorKey) {
    if (errorKey) {
      this.props.setSnackbar(this.msgs[errorKey]);
      return;
    }

    Object.keys(this.state[errorGroup]).every(errorKey => {
      if (this.state[errorGroup][errorKey]) {
        this.props.setSnackbar(this.msgs[errorKey]);
        return false;
      }
      return true;
    });
  }

  // Strategy Functions
  noWorkingStrategy() {
    return Object.keys(this.state.strategyChangesMade).every(
      i => !this.state.strategyChangesMade[i],
    );
  }

  addStrategy() {
    // Prevent adding a new strategy if there are working changes or errors on an existing strategy
    if (!this.noWorkingStrategy()) return this.triggerSnackbar(null, 'addStrategy');

    const strategies = [
      ...this.state.strategies,
      {
        strategy_id: null,
        name: '',
        description: '',
        labels: null,
        bid: null,
        cogs: null,
        roi_goal: null,
        datetime_start: null,
        datetime_end: null,
        budget_amount: '',
        priority: 'normal',
        utm_query_string: null,
        is_enabled: true,
        merch_id: this.orgId,
        triggers: {
          brands: [],
          categories: [],
          publishers: [],
          skus: [],
        },
        visibility: 'public',
        fee_type: 'marketplace',
      },
    ];
    this[`strategy_${strategies.length - 1}`] = React.createRef();

    this.setState({ strategies }, () => {
      const strategyRef = this[`strategy_${this.state.strategies.length - 1}`].current
        .strategyContainer.current;
      strategyRef.scrollIntoView({ behavior: 'smooth' });
      strategyRef.focus({ preventScroll: true });
      this.setStrategyChangesMade(true, this.state.strategies.length - 1);
    });
  }

  discardStrategy() {
    this.setState({
      strategies: this.state.strategies.filter(strat => strat.strategy_id),
    });
  }

  saveStrategy(strategy) {
    if (this.state.saveInProgress) return;
    if (!this.hasNoError('strategyErrors')) {
      // Temporarily remove validation on request of Biz
      // return this.triggerSnackbar('strategyErrors');
    }

    // Update types before sending request
    strategy.budget_amount = parseFloat(strategy.budget_amount);
    strategy.bid = parseFloat(strategy.bid);
    strategy.roi_goal = parseFloat(strategy.roi_goal);
    strategy.publisher_epc = strategy.publisher_epc ? parseFloat(strategy.publisher_epc) : null;

    if (strategy.cogs) {
      strategy.cogs = parseFloat(strategy.cogs / 100);
    }
    if (!strategy.labels) {
      strategy.labels = null;
    }
    // Map products to skus for api PUT/POST
    strategy.triggers.skus = strategy.triggers.skus.map(item => item.sku);

    // Remove unnecessary properties
    const { datetime_created, datetime_updated, ...strategyToUpdate } = strategy;

    this.setState({ saveInProgress: true });
    api
      .put(this.strategiesEndpoint, strategyToUpdate)
      .then(resp => {
        this.setMerchantStrategies({ strategies: resp.data.data }, true);
        this.setState({ ...this.defaultState });
        this.triggerSnackbar(null, 'saved');
        this.setState({
          saveInProgress: false,
          errorModal: null,
        });
      })
      .catch(e => {
        const errorMessage =
          e && e.message
            ? e.message.match('pub trigger')
              ? this.cogsError
              : this.missingError
            : null;
        this.setState({
          saveInProgress: false,
          errorModal: errorMessage,
        });
      });
  }

  setStrategyChangesMade(value, strategyId) {
    this.setState({
      strategyChangesMade: {
        ...this.state.setStrategyChangesMade,
        [strategyId]: value,
      },
    });
  }

  updatePage = page => {
    this.setState({ pageInfo: { ...this.state.pageInfo, page }, loading: true });
  };

  render() {
    if (this.state.loading) return <Loader loading={this.state.loading} />;

    const merchantStrategies = this.state.strategies.map((strategy, idx) => {
      // Hides admin-only campaigns from non-admins
      return strategy.visibility !== 'admin' || session.isAdminView ? (
        <Strategy
          ref={this[`strategy_${idx}`]}
          key={strategy.strategy_id}
          strategy={strategy}
          saveStrategy={this.saveStrategy.bind(this)}
          discardStrategy={this.discardStrategy.bind(this)}
          setChangesMade={this.setStrategyChangesMade.bind(this)}
          hasNoError={this.hasNoError.bind(this)}
          setError={this.setError.bind(this)}
          pricingModel={this.state.merchantInfo.pricing_model}
          defaultStrategyType={this.state.strategyType}
          noWorkingStrategy={this.noWorkingStrategy.bind(this)}
          initialPubs={this.state.initialPubs}
          saveInProgress={this.state.saveInProgress}
          showBudgetSpent={this.showBudgetSpent}
        />
      ) : null;
    });

    const errorModal = this.state.errorModal ? (
      <Modal
        options={{
          identifier: 'strategy-confirmation',
          displayMsg: 'Save Error',
          displayTxt: this.state.errorModal,
          save: {
            txt: 'Okay',
            callback: () => this.setState({ errorModal: null }),
          },
        }}
      />
    ) : null;

    return (
      <div className="merchant-strategies-container">
        <h3>Default Strategy</h3>
        <DefaultStrategy
          regexes={{ roi: this.roiRegex, currency: this.currencyRegex }}
          maxBudget={this.maxBudget}
          maxStrategyValue={this.maxStrategyValue}
          snackbarMsgs={this.msgs}
        />
        <div className="all-strategies-header">
          <h3>All Campaigns</h3>
          <button className="add-strategy-btn" onClick={() => this.addStrategy()}>
            Add Campaign
          </button>
        </div>
        {merchantStrategies}
        {errorModal}
        <Pagination
          showPagination={true}
          currentPage={this.state.pageInfo.page}
          totalItems={this.state.pageInfo.total_items}
          perPage={this.state.pageInfo.per_page}
          updatePage={this.updatePage}
          reducedPagination={true}
        />
      </div>
    );
  }
}

const mapDispatchToProps = dispatch => {
  return {
    setSnackbar: msg => dispatch(setSnackbar(msg)),
  };
};

export default withRouter(connect(null, mapDispatchToProps)(MerchantStrategies));
